import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { signInAnonymously } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from './firebase-config';
import {
  resolveStockControllersCollectionPath,
  resolveStockSheetsCollectionPath,
  resolveStockSheetDocPath,
  resolveStockSheetLinesCollectionPath,
  resolveStockControlsCollectionPath,
} from './core/company/companyPaths';
import StockOperatorTasksView from './modules/stock/components/StockOperatorTasksView';
import StockOperatorSheetView from './modules/stock/components/StockOperatorSheetView';

const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const normalizePin = (value = '') => String(value || '').trim();
const normalizeKey = (value = '') => String(value || '').trim();

const toMillis = (value) => {
  if (!value) return 0;
  if (value?.toDate) return value.toDate().getTime();
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? time : 0;
};

const mapControllerAccessError = (error, fallbackMessage) => {
  const rawMessage = String(error?.message || '').trim();
  if (
    rawMessage.includes('Missing or insufficient permissions') ||
    rawMessage.includes('permission-denied') ||
    rawMessage.includes('PERMISSION_DENIED')
  ) {
    return 'No tengo permisos para operar esta vista. Revisá reglas Firebase para acceso de controlador.';
  }
  if (rawMessage.includes('auth/operation-not-allowed') || rawMessage.includes('Anonymous sign-in')) {
    return 'Anonymous Auth no está habilitado o no pudo iniciar sesión para este acceso.';
  }
  return rawMessage || fallbackMessage;
};

const buildControllerDisplayName = (controller = {}) => {
  const displayName = String(controller.displayName || '').trim();
  if (displayName) return displayName;
  return [controller.firstName, controller.lastName].filter(Boolean).join(' ').trim() || 'Controlador de stock';
};

const normalizeLine = (line = {}) => {
  const expectedQuantity = line.expectedQuantity == null ? null : Number(line.expectedQuantity || 0);
  const countedQuantity = line.countedQuantity == null ? null : Number(line.countedQuantity || 0);
  const rawDifference = line.differenceQuantity ?? line.differenceValue;
  const differenceValue = rawDifference == null
    ? (countedQuantity == null || expectedQuantity == null ? null : countedQuantity - expectedQuantity)
    : Number(rawDifference || 0);
  const productName = line.productName || line.productNameSnapshot || 'Producto';
  const variantLabel = line.codeLabel || line.productVariantSnapshot || line.productSkuSnapshot || 'Sin variante visible';
  const noteLabel = String(line.noteLabel ?? line.observation ?? '').trim() || 'Sin observación';
  const status = String(line.status || (countedQuantity == null ? 'pending' : differenceValue === 0 ? 'checked_ok' : 'checked_diff')).trim() || 'pending';

  return {
    id: line.id,
    companyId: line.companyId || '',
    sheetId: line.sheetId || '',
    productId: line.productId || '',
    product: { id: line.productId || '', name: productName },
    status,
    addedDuringControl: status === 'added_during_control',
    productName,
    codeLabel: variantLabel,
    categoryLabel: String(line.categoryLabel || line.productCategorySnapshot || '').trim(),
    expectedQuantity,
    expectedLabel: line.expectedLabel || (line.expectedQuantity == null ? 'Sin base' : `${line.expectedQuantity}`),
    countedQuantity,
    countedLabel: line.countedLabel || (line.countedQuantity == null ? '-' : `${line.countedQuantity}`),
    differenceValue,
    readingLabel: line.readingLabel || (countedQuantity == null ? 'Todavía sin conteo cargado' : differenceValue === 0 ? 'Coincide con el sistema' : 'Hay diferencia para revisar'),
    noteLabel,
  };
};

const buildSheetCard = (sheet = {}, lines = []) => {
  const normalizedLines = lines.map(normalizeLine);
  const completedLines = normalizedLines.filter((line) => ['checked_ok', 'checked_diff'].includes(line.status)).length;
  const pendingLines = normalizedLines.filter((line) => line.status === 'pending').length;
  const diffLines = normalizedLines.filter((line) => line.status === 'checked_diff').length;

  return {
    id: sheet.id,
    ...sheet,
    title: sheet.title || 'Planilla',
    locationLabel: sheet.sourceType === 'category_control'
      ? `Categoría ${sheet.sourceCategorySnapshot || ''}`.trim()
      : sheet.sourceSummarySnapshot || 'Control físico',
    dateLabel: sheet.sheetDate || 'Sin fecha',
    totalLines: normalizedLines.length || Number(sheet.linesCount || 0),
    pendingLines,
    completedLines,
    diffLines,
  };
};

const BrandShell = ({ children }) => (
  <div className="min-h-screen bg-[linear-gradient(180deg,_#020617_0%,_#0f172a_28%,_#e2e8f0_28%,_#f8fafc_100%)]">
    <div className="mx-auto max-w-7xl px-3 py-4 md:px-6 md:py-6">{children}</div>
  </div>
);

const TopHeader = ({ companyId, controllerName }) => (
  <div className="mb-4 overflow-hidden rounded-[28px] border border-white/10 bg-slate-950 text-white shadow-[0_20px_60px_rgba(2,6,23,0.35)]">
    <div className="bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.22),_transparent_35%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.14),_transparent_28%),linear-gradient(135deg,_rgba(15,23,42,1),_rgba(2,6,23,1))] px-5 py-5 md:px-6 md:py-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-200">
            <span className="h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
            G-CENTRA V3 · Control de stock
          </div>
          <h1 className="mt-3 text-2xl font-black tracking-tight md:text-3xl">Vista exclusiva del controlador</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
            Operá tus planillas asignadas desde una vista compacta, clara y lista para trabajar desde cualquier explorador.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:min-w-[360px]">
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Empresa</p>
            <p className="mt-2 text-sm font-bold text-white">{companyId || 'G-CENTRA'}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Controlador</p>
            <p className="mt-2 text-sm font-bold text-cyan-200">{controllerName || 'Controlador de stock'}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StockControllerAccessPage = ({ companyAccessKey, controllerAccessKey, onBackHome }) => {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [controllerRecord, setControllerRecord] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [activeSheetLines, setActiveSheetLines] = useState([]);
  const [sheetSearch, setSheetSearch] = useState('');
  const [sheetFilter, setSheetFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  const resolvedCompanyId = useMemo(() => normalizeKey(companyAccessKey), [companyAccessKey]);
  const resolvedControllerKey = useMemo(() => normalizeKey(controllerAccessKey), [controllerAccessKey]);

  const collectionPaths = useMemo(() => {
    if (!resolvedCompanyId) return null;
    return {
      controllers: resolveStockControllersCollectionPath(resolvedCompanyId),
      sheets: resolveStockSheetsCollectionPath(resolvedCompanyId),
      sheetLines: resolveStockSheetLinesCollectionPath(resolvedCompanyId),
      controls: resolveStockControlsCollectionPath(resolvedCompanyId),
    };
  }, [resolvedCompanyId]);

  useEffect(() => { setLoading(false); }, []);

  const ensureFirebaseSession = useCallback(async () => {
    if (!auth) throw new Error('Firebase Auth no está disponible.');
    if (auth.currentUser) return auth.currentUser;
    const credentials = await signInAnonymously(auth);
    return credentials.user;
  }, []);

  const loadLinesForSheet = useCallback(async (sheetId) => {
    if (!db || !collectionPaths?.sheetLines || !sheetId) return [];
    const linesQuery = query(collection(db, collectionPaths.sheetLines), where('sheetId', '==', sheetId));
    const snapshot = await getDocs(linesQuery);
    return snapshot.docs.map((lineDoc) => ({ id: lineDoc.id, ...lineDoc.data() })).sort((a, b) => Number(a.lineOrder || 0) - Number(b.lineOrder || 0));
  }, [collectionPaths]);

  const loadAssignedSheets = useCallback(async (controllerId) => {
    if (!db || !collectionPaths?.sheets || !controllerId) return [];
    const sheetsQuery = query(collection(db, collectionPaths.sheets), where('assignedControllerId', '==', controllerId));
    const snapshot = await getDocs(sheetsQuery);
    const rawSheets = snapshot.docs.map((sheetDoc) => ({ id: sheetDoc.id, ...sheetDoc.data() })).sort((a, b) => toMillis(b.updatedAt || b.assignedAt || b.createdAt) - toMillis(a.updatedAt || a.assignedAt || a.createdAt));
    return Promise.all(rawSheets.map(async (sheet) => buildSheetCard(sheet, await loadLinesForSheet(sheet.id))));
  }, [collectionPaths, loadLinesForSheet]);

  const verifyControllerAccess = useCallback(async () => {
    if (!db || !collectionPaths?.controllers) throw new Error('Firestore no está disponible.');
    if (!resolvedCompanyId || !resolvedControllerKey) throw new Error('El link del controlador está incompleto.');
    await ensureFirebaseSession();
    const controllersQuery = query(collection(db, collectionPaths.controllers), where('accessSlug', '==', resolvedControllerKey), limit(1));
    const snapshot = await getDocs(controllersQuery);
    if (snapshot.empty) throw new Error('No encontré el controlador para este enlace.');
    const controllerDoc = snapshot.docs[0];
    const controller = { id: controllerDoc.id, ...controllerDoc.data() };
    if (controller.active === false) throw new Error('Este acceso de controlador está desactivado.');
    if (normalizePin(controller.pin) !== normalizePin(pinInput)) throw new Error('PIN incorrecto.');
    const normalizedController = { ...controller, displayName: buildControllerDisplayName(controller) };
    return { controller: normalizedController, sheets: await loadAssignedSheets(controller.id) };
  }, [collectionPaths, ensureFirebaseSession, loadAssignedSheets, pinInput, resolvedCompanyId, resolvedControllerKey]);

  const handleUnlock = async (event) => {
    event?.preventDefault?.();
    setLoading(true);
    try {
      const payload = await verifyControllerAccess();
      setControllerRecord(payload.controller || null);
      setSheets(Array.isArray(payload.sheets) ? payload.sheets : []);
      setUnlocked(true);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(mapControllerAccessError(error, 'No pude validar este acceso.'));
    } finally {
      setLoading(false);
    }
  };

  const refreshSheets = useCallback(async () => {
    if (!controllerRecord?.id) return;
    setSheets(await loadAssignedSheets(controllerRecord.id));
  }, [controllerRecord, loadAssignedSheets]);

  const handleOpenSheet = useCallback(async (sheet) => {
    if (!sheet?.id) return;
    setSaving(true);
    try {
      await ensureFirebaseSession();
      const lines = await loadLinesForSheet(sheet.id);
      setActiveSheet(buildSheetCard(sheet, lines));
      setActiveSheetLines(lines.map(normalizeLine));
      setSheetSearch('');
      setSheetFilter('all');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(mapControllerAccessError(error, 'No pude abrir la planilla.'));
    } finally {
      setSaving(false);
    }
  }, [ensureFirebaseSession, loadLinesForSheet]);

  const handleInlineChange = useCallback((lineId, updates = {}) => {
    setActiveSheetLines((prev) => prev.map((line) => {
      if (line.id !== lineId) return line;
      const countedQuantity = Object.prototype.hasOwnProperty.call(updates, 'countedQuantity') ? (updates.countedQuantity === '' ? null : updates.countedQuantity) : line.countedQuantity;
      const expectedQuantity = line.expectedQuantity == null ? null : Number(line.expectedQuantity);
      const differenceValue = countedQuantity == null || expectedQuantity == null ? null : Number(countedQuantity) - expectedQuantity;
      const nextStatus = countedQuantity == null ? 'pending' : differenceValue === 0 ? 'checked_ok' : 'checked_diff';
      return { ...line, ...updates, countedQuantity, countedLabel: countedQuantity == null ? '-' : `${countedQuantity}`, differenceValue, readingLabel: countedQuantity == null ? 'Todavía sin conteo cargado' : differenceValue === 0 ? 'Coincide con el sistema' : 'Hay diferencia para revisar', status: nextStatus };
    }));
  }, []);

  const persistSheet = useCallback(async ({ finalize = false } = {}) => {
    if (!activeSheet?.id || !controllerRecord?.id || !collectionPaths) return;
    if (finalize && activeSheet.status === 'completed') return;
    setSaving(true);
    try {
      const currentUser = await ensureFirebaseSession();
      const batch = writeBatch(db);
      const now = new Date();
      const normalizedForWrite = activeSheetLines.map((line) => {
        const countedQuantity = line.countedQuantity == null || line.countedQuantity === '' ? null : Number(line.countedQuantity);
        const expectedQuantity = line.expectedQuantity == null ? null : Number(line.expectedQuantity);
        const differenceQuantity = countedQuantity == null || expectedQuantity == null ? null : countedQuantity - expectedQuantity;
        const status = countedQuantity == null ? 'pending' : differenceQuantity === 0 ? 'checked_ok' : 'checked_diff';
        return { ...line, countedQuantity, differenceQuantity, status, noteLabel: String(line.noteLabel || '').trim() || 'Sin observación' };
      });

      normalizedForWrite.forEach((line) => {
        batch.update(doc(db, collectionPaths.sheetLines, line.id), {
          countedQuantity: line.countedQuantity,
          differenceQuantity: line.differenceQuantity,
          observation: line.noteLabel === 'Sin observación' ? '' : line.noteLabel,
          noteLabel: line.noteLabel === 'Sin observación' ? '' : line.noteLabel,
          status: line.status,
          reviewedAt: line.countedQuantity == null ? null : now,
          updatedAt: serverTimestamp(),
          lastControllerWriteAt: serverTimestamp(),
          lastControllerWriteSource: 'stock-controller-view',
          lastControllerId: controllerRecord.id,
          lastControllerAccessSlug: controllerRecord.accessSlug || resolvedControllerKey,
          lastControllerAuthUid: currentUser?.uid || null,
        });
      });

      const completedLines = normalizedForWrite.filter((line) => ['checked_ok', 'checked_diff'].includes(line.status)).length;
      const diffLines = normalizedForWrite.filter((line) => line.status === 'checked_diff').length;
      const nextSheetStatus = finalize ? 'completed' : completedLines > 0 ? 'in_progress' : (activeSheet.status || 'assigned');

      batch.update(doc(db, resolveStockSheetDocPath(resolvedCompanyId, activeSheet.id)), {
        status: nextSheetStatus,
        reviewedLinesCount: completedLines,
        differenceLinesCount: diffLines,
        completedAt: finalize ? now : activeSheet.completedAt || null,
        startedAt: completedLines > 0 ? (activeSheet.startedAt || now) : activeSheet.startedAt || null,
        updatedAt: serverTimestamp(),
        lastControllerWriteAt: serverTimestamp(),
        lastControllerWriteSource: 'stock-controller-view',
        lastControllerId: controllerRecord.id,
        lastControllerAccessSlug: controllerRecord.accessSlug || resolvedControllerKey,
        lastControllerAuthUid: currentUser?.uid || null,
      });

      if (finalize) {
        normalizedForWrite.filter((line) => line.productId && line.countedQuantity != null).forEach((line) => {
          const controlRef = doc(collection(db, collectionPaths.controls));
          batch.set(controlRef, {
            startTime: activeSheet.createdAt || activeSheet.startedAt || now,
            endTime: now,
            countDetails: JSON.stringify([{ id: line.id, name: line.productName, stockSystem: line.expectedQuantity ?? 0, stockPhysical: line.countedQuantity, difference: line.differenceQuantity ?? 0, observation: line.noteLabel === 'Sin observación' ? '' : line.noteLabel }]),
            type: 'sheet_line',
            productId: line.productId,
            productName: line.productName,
            companyId: resolvedCompanyId,
            controllerId: controllerRecord.id,
            controllerDisplayNameSnapshot: controllerRecord.displayName || buildControllerDisplayName(controllerRecord),
            controllerPhotoURLSnapshot: String(controllerRecord.photoURL || '').trim() || null,
            sheetId: activeSheet.id,
            sheetTitleSnapshot: activeSheet.title || 'Planilla',
            completedByUserId: controllerRecord.id,
            completedByAuthUid: currentUser?.uid || null,
            sourceType: activeSheet.sourceType || 'category_control',
            sourceCategorySnapshot: activeSheet.sourceCategorySnapshot || '',
            createdAt: serverTimestamp(),
            createdBySource: 'stock-controller-view',
          });
        });
      }

      await batch.commit();
      const refreshedLines = await loadLinesForSheet(activeSheet.id);
      setActiveSheet(buildSheetCard({ ...activeSheet, status: nextSheetStatus, completedAt: finalize ? now : activeSheet.completedAt }, refreshedLines));
      setActiveSheetLines(refreshedLines.map(normalizeLine));
      await refreshSheets();
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(mapControllerAccessError(error, 'No pude guardar la planilla.'));
    } finally {
      setSaving(false);
    }
  }, [activeSheet, activeSheetLines, collectionPaths, controllerRecord, ensureFirebaseSession, loadLinesForSheet, refreshSheets, resolvedCompanyId, resolvedControllerKey]);

  const filteredLines = useMemo(() => {
    const search = String(sheetSearch || '').trim().toLowerCase();
    return activeSheetLines.filter((line) => {
      if (sheetFilter !== 'all' && line.status !== sheetFilter) return false;
      if (!search) return true;
      return [line.productName, line.codeLabel, line.categoryLabel].join(' ').toLowerCase().includes(search);
    });
  }, [activeSheetLines, sheetFilter, sheetSearch]);

  if (loading) {
    return <BrandShell><div className="flex min-h-[72vh] items-center justify-center text-center text-white"><div><p className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">G-CENTRA V3</p><h1 className="mt-3 text-3xl font-black">Preparando acceso operativo</h1></div></div></BrandShell>;
  }

  if (errorMessage && !unlocked) {
    return (
      <BrandShell>
        <div className="flex min-h-[72vh] items-center justify-center">
          <div className="w-full max-w-xl overflow-hidden rounded-[30px] border border-white/10 bg-slate-950 text-white shadow-[0_24px_70px_rgba(2,6,23,0.45)]">
            <div className="border-b border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.18),_transparent_34%),linear-gradient(180deg,_rgba(15,23,42,1),_rgba(2,6,23,1))] px-6 py-6">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-300">Acceso no disponible</p>
              <h1 className="mt-2 text-2xl font-black">No pude abrir la vista del controlador</h1>
            </div>
            <div className="px-6 py-6"><p className="text-sm leading-6 text-slate-300">{errorMessage}</p><div className="mt-6 flex flex-col gap-3 sm:flex-row"><button onClick={() => setErrorMessage('')} className="rounded-2xl bg-cyan-300 px-4 py-3 text-sm font-bold text-slate-950">Reintentar PIN</button><button onClick={onBackHome} className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-bold text-white">Volver al inicio</button></div></div>
          </div>
        </div>
      </BrandShell>
    );
  }

  if (!unlocked) {
    return (
      <BrandShell>
        <div className="flex min-h-[72vh] items-center justify-center">
          <form onSubmit={handleUnlock} className="w-full max-w-xl overflow-hidden rounded-[32px] border border-white/10 bg-slate-950 text-white shadow-[0_24px_70px_rgba(2,6,23,0.45)]">
            <div className="border-b border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.22),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.14),_transparent_26%),linear-gradient(180deg,_rgba(15,23,42,1),_rgba(2,6,23,1))] px-6 py-7 md:px-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-200"><span className="h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />G-CENTRA V3 · Acceso Controlador</div>
              <h1 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">Control de stock operativo</h1>
              <p className="mt-3 max-w-lg text-sm leading-6 text-slate-300">Ingresá con tu PIN y operá tus controles asignados desde cualquier dispositivo.</p>
            </div>
            <div className="px-6 py-7 md:px-8">
              <div className="grid gap-4 md:grid-cols-2"><div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Empresa</p><p className="mt-2 text-sm font-bold text-white">{resolvedCompanyId || 'G-CENTRA'}</p></div><div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Acceso</p><p className="mt-2 text-sm font-bold text-cyan-200">Controlador de stock</p></div></div>
              <div className="mt-6"><label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">PIN de acceso</label><input type="password" inputMode="numeric" value={pinInput} onChange={(event) => setPinInput(event.target.value)} className="mt-2 w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4 text-lg font-semibold tracking-[0.35em] text-white outline-none placeholder:text-slate-600 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-500/40" placeholder="••••" /></div>
              {errorMessage ? <p className="mt-3 text-sm text-rose-300">{errorMessage}</p> : null}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row"><button type="submit" className="rounded-2xl bg-cyan-300 px-5 py-3.5 text-sm font-bold text-slate-950 transition-transform hover:-translate-y-0.5">Ingresar al control</button><button type="button" onClick={onBackHome} className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3.5 text-sm font-semibold text-slate-200">Volver</button></div>
            </div>
          </form>
        </div>
      </BrandShell>
    );
  }

  if (activeSheet) {
    return (
      <BrandShell>
        <TopHeader companyId={resolvedCompanyId} controllerName={controllerRecord?.displayName} />
        {errorMessage ? <div className="mb-4 rounded-2xl border border-rose-300/60 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{errorMessage}</div> : null}
        <StockOperatorSheetView sheet={activeSheet} lines={filteredLines} searchTerm={sheetSearch} setSearchTerm={setSheetSearch} lineFilter={sheetFilter} setLineFilter={setSheetFilter} onBack={() => setActiveSheet(null)} onShowHistory={() => {}} onInlineChange={handleInlineChange} onOpenAddProduct={null} onSaveSheet={() => persistSheet({ finalize: false })} onFinalizeSheet={() => persistSheet({ finalize: true })} saving={saving} Icon={Icon} hideAddProduct hideScan backLabel="Volver a mis planillas" />
      </BrandShell>
    );
  }

  return (
    <BrandShell>
      <TopHeader companyId={resolvedCompanyId} controllerName={controllerRecord?.displayName} />
      {errorMessage ? <div className="mb-4 rounded-2xl border border-rose-300/60 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{errorMessage}</div> : null}
      <StockOperatorTasksView controlSheets={sheets} onOpenSheet={handleOpenSheet} Icon={Icon} operatorLabel={controllerRecord?.displayName || 'Controlador de stock'} />
    </BrandShell>
  );
};

export default StockControllerAccessPage;
